import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO, { createWebPageSchema, createBreadcrumbSchema } from '../components/SEO.tsx';
import MemeCard from '../components/memes/MemeCard.tsx';
import LazyImage from '../components/LazyImage.tsx';
import AdSense, { InArticleAd, SidebarAd } from '../components/AdSense.tsx';
import { getMemeById, getRelatedMemes } from '../data/memeData.ts';

const MemeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  const meme = id ? getMemeById(id) : undefined;
  const relatedMemes = id ? getRelatedMemes(id, 4) : [];

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // 404 if meme not found
  if (!meme) {
    return (
      <div className="min-h-screen bg-background-primary pt-16 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
          <p className="text-gray-600 mb-8">
            {isKorean ? '밈을 찾을 수 없습니다' : 'Meme not found'}
          </p>
          <Link
            to="/memes"
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            {isKorean ? '밈 갤러리로 돌아가기' : 'Back to Meme Gallery'}
          </Link>
        </div>
      </div>
    );
  }

  const title = isKorean ? meme.titleKo : meme.title;
  const description = isKorean ? meme.descriptionKo : meme.description;
  const longDescription = isKorean ? meme.longDescriptionKo : meme.longDescription;
  const codingKnowledge = isKorean ? meme.codingKnowledgeKo : meme.codingKnowledge;

  const structuredData = [
    createWebPageSchema({
      title: `${title} - Meme`,
      description: description.slice(0, 160),
      url: `/memes/${meme.id}`
    }),
    createBreadcrumbSchema([
      { name: isKorean ? '홈' : 'Home', url: '/' },
      { name: isKorean ? '밈' : 'Memes', url: '/memes' },
      { name: title, url: `/memes/${meme.id}` }
    ]),
    // Article structured data for SEO
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      image: meme.thumbnail,
      datePublished: meme.createdAt,
      author: {
        '@type': 'Organization',
        name: 'Bearly Coding Lab'
      },
      description: description.slice(0, 160)
    }
  ];

  return (
    <div className="min-h-screen bg-background-primary pt-16">
      <SEO
        title={`${title} - Meme`}
        description={description.slice(0, 160)}
        keywords={[...meme.tags, 'meme', 'internet meme', title]}
        canonicalUrl={`/memes/${meme.id}`}
        ogImage={meme.thumbnail}
        ogImageAlt={title}
        structuredData={structuredData}
      />

      {/* Main Layout Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="lg:flex lg:gap-8">
          {/* Main Content */}
          <article className="lg:flex-1 max-w-4xl">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6 sm:mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-green-600 transition-colors">
                {isKorean ? '홈' : 'Home'}
              </Link>
            </li>
            <li><span className="mx-2">/</span></li>
            <li>
              <Link to="/memes" className="hover:text-green-600 transition-colors">
                {isKorean ? '밈' : 'Memes'}
              </Link>
            </li>
            <li><span className="mx-2">/</span></li>
            <li className="text-green-600 font-medium truncate max-w-[200px]">{title}</li>
          </ol>
        </nav>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Meme Images */}
          <div className="relative bg-gradient-to-br from-green-100 to-green-50">
            {meme.images.length === 1 ? (
              <LazyImage
                src={meme.images[0]}
                alt={title}
                className="w-full max-h-[600px] object-contain mx-auto"
              />
            ) : (
              <div className="space-y-4 p-4">
                {meme.images.map((img, index) => (
                  <LazyImage
                    key={index}
                    src={img}
                    alt={`${title} ${index + 1}`}
                    className="w-full max-h-[500px] object-contain mx-auto rounded-lg"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8 lg:p-10">
            {/* Header */}
            <header className="mb-6">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-gray-400 text-sm">
                  {new Date(meme.createdAt).toLocaleDateString(isKorean ? 'ko-KR' : 'en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                {title}
              </h1>
            </header>

            {/* Short Description */}
            <div className="prose prose-lg max-w-none mb-6">
              <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
                {description}
              </p>
            </div>

            {/* Long Description */}
            {longDescription && (
              <div className="prose prose-lg max-w-none mb-8">
                {longDescription.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-gray-600 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            )}

            {/* Coding Knowledge Section */}
            {codingKnowledge && (
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 sm:p-8 mb-8 border border-green-100">
                <h2 className="text-xl sm:text-2xl font-bold text-green-800 mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  {isKorean ? '코딩 지식' : 'Coding Knowledge'}
                </h2>
                <div className="prose prose-green max-w-none">
                  {codingKnowledge.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="text-green-900 leading-relaxed mb-4 last:mb-0">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* AdSense - In-Article Ad */}
            <div className="mb-8 -mx-6 sm:-mx-8 lg:-mx-10 px-6 sm:px-8 lg:px-10 py-4 bg-gray-50">
              <InArticleAd adSlot="7072841007" />
            </div>

            {/* Tags */}
            <div className="mb-8">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                {isKorean ? '태그' : 'Tags'}
              </h2>
              <div className="flex flex-wrap gap-2">
                {meme.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-green-50 text-green-700 text-sm font-medium rounded-full hover:bg-green-100 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Share Button */}
            <div className="mb-8 pt-6 border-t border-gray-200">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                {isKorean ? '공유하기' : 'Share'}
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    alert(isKorean ? '링크가 복사되었습니다!' : 'Link copied!');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {isKorean ? '링크 복사' : 'Copy Link'}
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* Ad before Related Memes */}
        <div className="mt-12 mb-8">
          <AdSense
            adSlot="5536575663"
            adFormat="horizontal"
            className="min-h-[100px]"
          />
        </div>

        {/* Related Memes Section - For user retention */}
        {relatedMemes.length > 0 && (
          <section className="mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {isKorean ? '관련 밈' : 'Related Memes'}
            </h2>
            <div className="space-y-3 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6">
              {relatedMemes.map((relatedMeme) => (
                <MemeCard key={relatedMeme.id} meme={relatedMeme} />
              ))}
            </div>
          </section>
        )}

        {/* More Memes Button */}
        <div className="mt-12 text-center">
          <Link
            to="/memes"
            className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors shadow-lg hover:shadow-xl"
          >
            {isKorean ? '더 많은 밈 보기' : 'View More Memes'}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>

        {/* AdSense - Bottom of page */}
        <div className="mt-12">
          <AdSense
            adSlot="9767464719"
            adFormat="auto"
            className="min-h-[250px]"
          />
        </div>
          </article>

          {/* Sidebar - Desktop Only */}
          <aside className="hidden lg:block lg:w-80 flex-shrink-0">
            <div className="sticky top-24">
              <SidebarAd adSlot="9858786443" className="mb-6" />

              {/* Additional sidebar content can go here */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                <h3 className="font-bold text-green-800 mb-2">
                  {isKorean ? '🐻 Bearly Coding Lab' : '🐻 Bearly Coding Lab'}
                </h3>
                <p className="text-sm text-green-700">
                  {isKorean
                    ? '코딩과 개발을 재미있게 배워보세요!'
                    : 'Learn coding and development in a fun way!'}
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>

    </div>
  );
};

export default MemeDetail;
